import React from 'react';
import classNames from 'classnames';

import Field, { hasDataInFields } from '../PageBuilder/Field';
import SectionContainer from '../PageBuilder/SectionBuilder/SectionContainer';
import { IconEyes } from '../../components';

import css from './CMSPageNews.module.css';

/**
 * Resolve an image reference from the included assets array
 * @param {Object} imageRef - The image reference with _ref.id
 * @param {Array} included - The included assets array
 * @returns {Object|null} - Full imageAsset object with id, type, and attributes
 */
const resolveImageRef = (imageRef, included) => {
  if (!imageRef?._ref?.id || !included) return null;

  const imageAsset = included.find(
    asset => asset.id === imageRef._ref.id && asset.type === 'imageAsset'
  );

  // Return the full imageAsset object which includes id, type, and attributes
  // This is the format expected by the Field component's exposeImageProps
  if (imageAsset?.attributes?.variants) {
    return imageAsset;
  }
  return null;
};

/**
 * Extract the first image from blog article sections
 * @param {Object} blogData - The blog article data
 * @returns {Object|null} - Media object with image data or null
 */
const extractFirstImageFromBlog = blogData => {
  if (!blogData?.sections) return null;

  const included = blogData.included || [];

  for (const section of blogData.sections) {
    // Check section-level media (e.g., background image)
    if (section.appearance?.backgroundImage?._ref?.id) {
      const resolved = resolveImageRef(section.appearance.backgroundImage, included);
      if (resolved) {
        return { fieldType: 'image', image: resolved, aspectRatio: '16/9' };
      }
    }

    // Check section-level media
    if (section.media?.fieldType === 'image' && section.media?.image?._ref?.id) {
      const resolved = resolveImageRef(section.media.image, included);
      if (resolved) {
        return { ...section.media, image: resolved };
      }
    }

    // Check blocks within section
    if (section.blocks) {
      for (const block of section.blocks) {
        if (block.media?.fieldType === 'image' && block.media?.image?._ref?.id) {
          const resolved = resolveImageRef(block.media.image, included);
          if (resolved) {
            return { ...block.media, image: resolved };
          }
        }
      }
    }
  }
  return null;
};

/**
 * Extract the first paragraph text from blog article sections
 * @param {Object} blogData - The blog article data
 * @returns {string|null} - First paragraph text or null
 */
const extractFirstParagraphFromBlog = blogData => {
  if (!blogData?.sections) return null;

  for (const section of blogData.sections) {
    // Check section-level text
    if (section.text?.content) {
      return section.text.content;
    }
    // Check blocks within section
    if (section.blocks) {
      for (const block of section.blocks) {
        if (block.text?.content) {
          return block.text.content;
        }
      }
    }
  }
  return null;
};

/**
 * Truncate text to first sentence (ending with . ! or ?)
 * @param {string} text - The text to truncate
 * @param {number} maxLength - Maximum length before truncating
 * @returns {string} - Truncated text
 */
const truncateToFirstSentence = (text, maxLength = 200) => {
  if (!text) return '';

  // Strip markdown formatting
  const plainText = text.replace(/[*_~`#\[\]]/g, '').trim();

  // Find first sentence ending
  const sentenceEndMatch = plainText.match(/^[^.!?]*[.!?]/);
  if (sentenceEndMatch) {
    const firstSentence = sentenceEndMatch[0].trim();
    if (firstSentence.length <= maxLength) {
      return firstSentence;
    }
  }

  // If no sentence ending found or sentence is too long, truncate at maxLength
  if (plainText.length <= maxLength) {
    return plainText;
  }

  // Truncate at word boundary
  const truncated = plainText.substring(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');
  return (lastSpace > 0 ? truncated.substring(0, lastSpace) : truncated) + '...';
};

/**
 * Extract title from blog article data
 * Prefers the article section's heading1 title over the meta page title
 * @param {Object} blogData - The blog article data
 * @returns {string} - Article title
 */
const extractTitleFromBlog = blogData => {
  // First, look for a title in article sections (sectionType: 'article')
  // These typically have the main article headline as heading1
  if (blogData?.sections) {
    for (const section of blogData.sections) {
      if (section.sectionType === 'article' && section.title?.content) {
        return section.title.content;
      }
    }

    // Fallback: check any section's title with heading1 fieldType
    for (const section of blogData.sections) {
      if (section.title?.fieldType === 'heading1' && section.title?.content) {
        return section.title.content;
      }
    }
  }

  // Fallback to meta title if no article title found
  if (blogData?.meta?.pageTitle?.content) {
    return blogData.meta.pageTitle.content;
  }

  return 'Untitled Article';
};

/**
 * Extract category from blog article data
 * @param {Object} blogData - The blog article data
 * @returns {string} - Category name
 */
const extractCategoryFromBlog = blogData => {
  // TODO: When category field is added to CMS, extract it from blogData
  // For now, return a default that matches the blog page display
  return 'Founder Story';
};

/**
 * Calculate estimated reading time based on word count in blog blocks
 * Average reading speed: 200 words per minute
 * @param {Object} blogData - The blog article data
 * @returns {number} - Estimated read time in minutes
 */
const calculateReadTimeFromBlog = blogData => {
  if (!blogData?.sections) return 1;

  let totalWords = 0;

  const countWordsInContent = content => {
    if (typeof content === 'string') {
      return content
        .trim()
        .split(/\s+/)
        .filter(word => word.length > 0).length;
    }
    return 0;
  };

  // Count words from all sections and blocks
  for (const section of blogData.sections) {
    // Count section-level text
    if (section.text?.content) {
      totalWords += countWordsInContent(section.text.content);
    }
    if (section.title?.content) {
      totalWords += countWordsInContent(section.title.content);
    }
    if (section.description?.content) {
      totalWords += countWordsInContent(section.description.content);
    }

    // Count words in blocks
    if (section.blocks) {
      for (const block of section.blocks) {
        if (block.text?.content) {
          totalWords += countWordsInContent(block.text.content);
        }
        if (block.title?.content) {
          totalWords += countWordsInContent(block.title.content);
        }
      }
    }
  }

  const minutes = Math.ceil(totalWords / 200);
  return minutes || 1; // Minimum 1 minute
};

/**
 * Custom section component for news page article listings
 * Renders dynamically discovered blog articles in a card-based grid layout
 */
const SectionColumnsNews = props => {
  const {
    sectionId,
    className,
    rootClassName,
    defaultClasses,
    title,
    description,
    appearance,
    callToAction,
    blocks = [],
    isInsideContainer = false,
    options,
  } = props;

  // If external mapping has been included for fields
  const fieldComponents = options?.fieldComponents;
  const blogArticlesData = options?.blogArticlesData || {};
  const blogArticleIds = options?.blogArticleIds || [];
  const fieldOptions = { fieldComponents };

  const hasHeaderFields = hasDataInFields([title, description, callToAction], fieldOptions);

  // Use dynamically discovered blog articles
  const hasBlogArticles = blogArticleIds.length > 0;

  // Helper to get description from blog data
  const getDescription = blogData => {
    const blogText = extractFirstParagraphFromBlog(blogData);
    if (blogText) {
      return truncateToFirstSentence(blogText);
    }
    return 'Read more about this article...';
  };

  // Helper to get media from blog data
  const getMediaData = blogData => {
    return extractFirstImageFromBlog(blogData);
  };

  return (
    <SectionContainer
      id={sectionId}
      className={className}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      {hasHeaderFields ? (
        <header className={classNames(defaultClasses.sectionDetails, css.newsPageSectionDetails)}>
          <Field
            data={title}
            className={classNames(defaultClasses.title, css.newsPageTitle)}
            options={fieldOptions}
          />
          <Field data={description} className={defaultClasses.description} options={fieldOptions} />
          <Field data={callToAction} className={defaultClasses.ctaButton} options={fieldOptions} />
        </header>
      ) : null}
      {hasBlogArticles ? (
        <div className={css.newsArticleGrid}>
          {blogArticleIds.map((blogId, index) => {
            const blogData = blogArticlesData[blogId];
            if (!blogData) return null;

            const category = extractCategoryFromBlog(blogData);
            const readTime = calculateReadTimeFromBlog(blogData);
            const articleTitle = extractTitleFromBlog(blogData);
            const articleDescription = getDescription(blogData);
            const mediaData = getMediaData(blogData);
            const link = `/p/${blogId}`;

            return (
              <a key={blogId} href={link} className={css.newsArticleCardLink}>
                <article className={css.newsArticleCard}>
                  {/* Article Image */}
                  {mediaData && (
                    <div className={css.newsArticleImage}>
                      <Field
                        data={mediaData}
                        sizes="(max-width: 767px) 100vw, (max-width: 1023px) 50vw, 25vw"
                        options={fieldOptions}
                      />
                    </div>
                  )}

                  {/* Article Content */}
                  <div className={css.newsArticleContent}>
                    {/* Category Badge and Read Time */}
                    <div className={css.newsArticleMeta}>
                      <span className={css.newsArticleCategory}>{category}</span>
                      <span className={css.newsArticleReadTime}>{readTime} min read</span>
                    </div>

                    {/* Article Title */}
                    <h3 className={css.newsArticleTitle}>{articleTitle}</h3>

                    {/* Article Description */}
                    <p className={css.newsArticleDescription}>{articleDescription}</p>

                    {/* Read More Button */}
                    <span className={css.newsArticleReadMore}>
                      <IconEyes className={css.newsArticleIcon} />
                      <span className={css.newsArticleReadMoreText}>read more</span>
                    </span>
                  </div>
                </article>
              </a>
            );
          })}
        </div>
      ) : null}
    </SectionContainer>
  );
};

export default SectionColumnsNews;

